import Highcharts, { Chart } from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { MutableRefObject, useCallback, useContext, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import ResponsiveContext, { ScreenType } from "../../../../contexts/ResponsiveContext"
import { IZone } from "../../../../typings/IZone"
import { IZoneData } from "../../../../typings/IZoneData"
import { SeriesType } from "../../../../typings/SeriesType"
import { ChartHelper } from "../../../../utils/chartHelper"
import styles from "./StandardGraphs.module.scss"
import { useDoubleTap } from "../../../../hooks/useDoubleTap"
import { isTwoPaddle, TrainingSport } from "../../../../typings/TrainingSport"
import { ChartTooltipContent } from "../ChartTooltipContent/ChartTooltipContent"
import { LineChart, standardLineChartOptions } from "../LineChart/LineChart"
import { chartHeight } from "../ChartHeight"
import { IUserSeriesData } from "../../../../typings/IUserSeries"
import { UserSeriesUnit } from "../../../../typings/UserSeriesUnit"
import { LactateChart } from "../LactateChart/LactateChart"
import { IAthlete } from "../../../../typings/IAthlete"
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../base/Tooltip/Tooltip"
import { InfoTooltip } from "../../InfoTooltip/InfoTooltip"
import { InfoTooltipButton } from "../../InfoTooltipButton/InfoTooltipButton"

interface IStandardGraphs {
  heartRateSeries: (number[][] | null)[]
  heartRateMax: number
  powerSeries: (number[][] | null)[]
  powerMax: number
  speedSeries: number[][] | null
  speedMax: number
  tempoSeries: number[][] | null
  tempoMax: number
  tempo500Series: number[][] | null
  tempo500Max: number
  strokeSeries: number[][] | null
  distancePerStrokeSeries: number[][] | null
  distancePerStrokeMax: number
  strokeMax: number
  forceLeftSeries: (number[][] | null)[]
  forceRightSeries: (number[][] | null)[]
  forceMax: number
  distanceSeries: number[][] | null
  hrZonesData?: IZoneData[]
  speedZonesData?: IZoneData[]
  tempoZonesData?: IZoneData[]
  tempo500ZonesData?: IZoneData[]
  forceZonesData?: IZoneData[]
  band: [number, number] | null
  detailedBand: [number, number] | null
  charts: SeriesType[]
  sliceStart: number
  sliceEnd: number
  hrZones?: IZone[]
  sport: TrainingSport
  hasZones: boolean
  isTracker: boolean
  xMax: number
  cursor: MutableRefObject<number>
  showCrosshair: boolean,
  userSeries?: IUserSeriesData[],
  athletes: IAthlete[],
  plotLines: number[],
  onSelectionChange: (selection: [number, number]) => void
  onZoomChange: (zoom: [number, number]) => void
  onCursorChange: (cursor: number) => void
  onMouseOver: (over: boolean) => void
  onEditUserSeries: (index: number) => void
  onFullscreenClick: (type: SeriesType) => void
}

export const forceSeriesColors = [
  "#3AB3E5", "#e34a4a",
  "#51e53a", "#dcd800",
  "#ff7300", "#d954e8",
  "#f80f78", "#858585",
  "#005173", "#135d00"
]
export const zoneWidth = 200
const zoneChartOptions = ChartHelper.getZoneChartOptions(chartHeight)

export const tempoFormatter = (x: number) => Highcharts.dateFormat("%M:%S", x * 60 * 1000)
const forceFormatter = (x: number) => x.toFixed(0).replace('-0', '0')

function plotLineMapper(x: number) {
  return {
    color: '#000000',
    width: 1,
    value: x
  }
}

export const StandardGraphs = ({
  heartRateSeries,
  heartRateMax,
  powerSeries,
  powerMax,
  speedSeries,
  speedMax,
  tempoSeries,
  tempoMax,
  tempo500Series,
  tempo500Max,
  strokeSeries,
  strokeMax,
  distancePerStrokeSeries,
  distancePerStrokeMax,
  forceLeftSeries,
  forceRightSeries,
  forceMax,
  distanceSeries,
  hrZones,
  band,
  detailedBand,
  charts,
  cursor,
  onSelectionChange,
  onZoomChange,
  sliceStart,
  sliceEnd,
  hrZonesData,
  speedZonesData,
  tempoZonesData,
  tempo500ZonesData,
  forceZonesData,
  sport,
  xMax,
  hasZones,
  isTracker,
  showCrosshair,
  userSeries,
  athletes,
  plotLines,
  onCursorChange,
  onMouseOver,
  onEditUserSeries,
  onFullscreenClick
}: IStandardGraphs) => {
  const { t } = useTranslation()
  const chartRefs = useRef<any[]>([])
  const zoneChartRefs = useRef<any[]>([])
  const { windowSize, screenType } = useContext(ResponsiveContext)

  const mouseMove = useCallback((e: PointerEvent | MouseEvent | TouchEvent) => {
    // See: https://www.highcharts.com/demo/highcharts/synchronized-charts
    // and https://www.highcharts.com/forum/viewtopic.php?t=33438
    let charts: Chart[] = chartRefs.current
      .filter(chart => chart !== null && chart !== undefined).map(c => c.chart as Chart)
    for (let i = 0; i < charts.length; i += 1) {
      try {
        const chart = charts[i]
        // if this the one we are targeting?
        const container = (e.target as any)?.closest(".highcharts-container")
        if (container === chart.container && chart.hoverPoint != null) {
          onCursorChange(chart.hoverPoint.x)
        }
      } catch (e) {
      }
    }
  }, [onCursorChange])

  useEffect(() => { // draw crosshair and show tooltip for every chart synchronized
    const t = window.setInterval(() => {
      let charts: Chart[] = chartRefs.current
        .filter(chart => chart !== null && chart !== undefined).map(c => c.chart as Chart)
      for (let i = 0; i < charts.length; i += 1) {
        try {
          const chart = charts[i]
          const closestPoint = chart.series.flatMap(x => x.points)
            .reduce((acc: any, p: any) => {
              const error = Math.abs(p.x - cursor.current)
              if (acc === null || acc.error > error)
                return {
                  point: p,
                  error
                }
              return acc
            }, { point: null, error: Infinity })
          if (!closestPoint.point) return
          closestPoint.point.onMouseOver()
          chart.xAxis[0].drawCrosshair({} as any, closestPoint.point)
        } catch (e) {
          console.log(e)
        }
      }
    }, 200)
    return () => clearInterval(t)
  }, [cursor])

  useEffect(() => { // set chart extremes synchronized
    let charts: Chart[] = chartRefs.current
      .filter(chart => chart !== null && chart !== undefined).map(c => c.chart as Chart)
    for (const chart of charts) {
      if (chart.xAxis?.[0]) {
        chart.xAxis[0].setExtremes(
          sliceStart,
          sliceEnd,
          true,
          false,
          { trigger: "syncExtremes" }
        )
      }
    }
  }, [sliceStart, sliceEnd])


  const setExtremes = useCallback((e: any) => { // See: https://www.highcharts.com/demo/highcharts/synchronized-charts
    if (e.trigger === "syncExtremes") return
    if (e.min != null && e.max != null) {
      onSelectionChange([e.min, e.min === e.max ? e.max + 1000 : e.max])
    }
  }, [onSelectionChange])

  const onResetZoom = useCallback((e: any | null) => {
    e?.preventDefault()
    e?.stopPropagation()
    onSelectionChange([0, xMax])
    let charts: Chart[] = chartRefs.current.filter(chart => chart !== null && chart !== undefined)
      .map(c => c.chart as Chart)
    for (let chart of charts) {
      chart.zoomOut()
    }
  }, [onSelectionChange, xMax])

  const getDistanceAtMs = useCallback((ms: number) => {
    return distanceSeries?.[Math.floor(ms / (isTracker ? 100 : 1000))]?.[1]
  }, [distanceSeries, isTracker])

  const tooltipFormatterFactory = useCallback(
    (valueFormatter?: ((x: number) => string), timeFormat?: string) => {
      return (x: number, points: any, unit: string) => {
        return <ChartTooltipContent
          x={x}
          distance={getDistanceAtMs(x)}
          points={points}
          valueFormatter={valueFormatter}
          timeFormat={timeFormat}
          unit={unit}
        />
      }
    },
    [getDistanceAtMs])

  const xAxisLabelFormatter = useCallback(
    (self: any) => {
      let s
      if (self.dateTimeLabelFormat)
        s = Highcharts.dateFormat(self.dateTimeLabelFormat, self.value)
      else
        s = "0"
      if (distanceSeries) {
        s += "<br />"
        const dp = distanceSeries[Math.floor(self.value / 1000)]
        const distance = dp?.[1] || 0
        s += Highcharts.numberFormat(distance, 1) + " km"
      }
      return `<div style="text-align: center">${s}</div>`
    },
    [distanceSeries],
  )

  const containerRef = useRef<HTMLDivElement>()

  const wheelListener = useCallback(
    (event: WheelEvent) => {
      if (sliceStart === 0 && sliceEnd === xMax)
        return
      event.preventDefault()
      event.stopPropagation()
      const start = sliceStart
      const end = sliceEnd
      // scale "1" scroll = 10% of the slice
      const tenPct = (end - start) / 10
      let d = event.deltaY / 100 * tenPct
      //clamp step
      if (start + d < 0) {
        d = 0 - start
      }
      if (end + d > xMax) {
        d = xMax - end
      }
      onZoomChange([start + d, end + d])
    },
    [sliceStart, sliceEnd, xMax, onZoomChange],
  )

  const handleContainerRef = useCallback((element: HTMLDivElement) => {
    if (element) {
      containerRef.current = element
      element.addEventListener("wheel", wheelListener, { passive: false })
    } else if (containerRef.current) {
      containerRef.current.removeEventListener("wheel", wheelListener)
      containerRef.current = undefined
    }
  }, [wheelListener])

  const setChartRef = (i: number) => (el: any) => chartRefs.current[i] = el
  const setZoneChartRef = (i: number) => (el: any) => zoneChartRefs.current[i] = el

  useEffect(() => {
    // highcharts need to be resized manually
    for (const ref of Object.values(chartRefs.current)) {
      ref.chart.reflow()
    }
    for (const ref of Object.values(zoneChartRefs.current)) {
      ref.chart.reflow()
    }
  }, [windowSize])

  const { onClick } = useDoubleTap(() => {
    onResetZoom(null)
  })

  let chartRefIndex = 0
  let zoneChartRefIndex = 0
  const mobileOrTablet = [ScreenType.mobile, ScreenType.tablet].includes(screenType)

  const forceSeries = useMemo(() =>
    forceLeftSeries.map((_, i) => [
      {
        data: forceLeftSeries[i],
        color: forceSeriesColors[(i * (isTwoPaddle(sport) ? 2 : 1)) % forceSeriesColors.length],
        dataGrouping: {
          approximation: "max"
        }
      },
      {
        data: forceRightSeries[i],
        color: forceSeriesColors[(i * (isTwoPaddle(sport) ? 2 : 1) + 1) % forceSeriesColors.length],
        dataGrouping: {
          approximation: "max"
        }
      }
    ]).flat().filter(x => x.data),
    [forceLeftSeries, forceRightSeries, sport])

  const bands = useMemo(() => {
    const res = []
    if (band)
      res.push({
        from: band[0],
        to: band[1],
        color: "rgba(140,140,140,0.07)",
      })
    if (detailedBand)
      res.push({
        from: detailedBand[0],
        to: detailedBand[1],
        color: "rgba(58, 179, 229,0.07)"
      })
    return res
  }, [band, detailedBand])

  const userLactateSeries = userSeries && userSeries
    .filter(x => x.unit === UserSeriesUnit.MillimolPerLiter)
  return (
    <div className={styles.container}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {userLactateSeries && userLactateSeries.map((x, i) => {
          return <LactateChart key={i}
            yAxisTitle={t("lactate series title", { position: i + 1 })}
            onEditClick={() => onEditUserSeries(i)}
            userSeries={x}
            athletes={athletes}
          />
        })}
        <div
          onMouseMove={(e) => {
            (mouseMove as any)(e)
            onMouseOver(true)
          }}
          onMouseOver={() => onMouseOver(true)}
          onMouseLeave={() => onMouseOver(false)}
          ref={handleContainerRef}
          onClick={onClick}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column"
          }}
        >
          {heartRateSeries && charts.includes(SeriesType.HEARTRATE) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory((x: number) => x.toFixed(0))}
                unit={"bpm"}
                valueFormatter={(x: number) => x.toFixed(0).replace('-0', '0')}
                showXLabels={!(charts.includes(SeriesType.SPEEDS) ||
                  charts.includes(SeriesType.TEMPOS) || charts.includes(SeriesType.STROKES) ||
                  charts.includes(SeriesType.PULLING_FORCE) || charts.includes(SeriesType.TEMPO_500S) ||
                  charts.includes(SeriesType.DISTANCEPERSTROKE) || charts.includes(SeriesType.POWERS))}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.HEARTRATE)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("heartrate"),
                    },
                    plotBands: charts.includes(SeriesType.HEARTRATEPLOTBANDS) ? hrZones?.map(z => ({
                      from: z.min,
                      to: z.max,
                      color: z.color,
                    })) : [],
                    min: 0,
                    max: heartRateMax
                  }
                }}
                series={heartRateSeries
                  .map((x, i) => (
                    {
                      data: x,
                      color: forceSeriesColors[(i * (isTwoPaddle(sport) ? 2 : 1)) % forceSeriesColors.length],
                      dataGrouping: {
                        approximation: "max"
                      }
                    }
                  )).filter(x => !!x.data)}
              />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip content={"info.hr chart"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {speedSeries && charts.includes(SeriesType.SPEEDS) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory(undefined,
                  isTracker ? "H:mm:ss.S" : undefined)}
                unit={"km/h"}
                valueFormatter={(x: number) => x.toFixed(1).replace('-0', '0')}
                showXLabels={!(charts.includes(SeriesType.TEMPOS) || charts.includes(SeriesType.STROKES) ||
                  charts.includes(SeriesType.PULLING_FORCE) || charts.includes(SeriesType.TEMPO_500S) ||
                  charts.includes(SeriesType.DISTANCEPERSTROKE) || charts.includes(SeriesType.POWERS))}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                series={speedSeries}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.SPEEDS)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("avg speed"),
                    },
                    min: 0,
                    max: speedMax
                  }
                }} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip
                  content={isTracker ? "info.speed chart tracker" : "info.speed chart"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {tempoSeries && charts.includes(SeriesType.TEMPOS) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory(tempoFormatter,
                  isTracker ? "H:mm:ss.S" : undefined)}
                unit={"/km"}
                valueFormatter={tempoFormatter}
                minMax={"min"}
                showXLabels={!(charts.includes(SeriesType.STROKES) ||
                  charts.includes(SeriesType.PULLING_FORCE) ||
                  charts.includes(SeriesType.TEMPO_500S) ||
                  charts.includes(SeriesType.DISTANCEPERSTROKE) ||
                  charts.includes(SeriesType.POWERS))}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                series={tempoSeries}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.TEMPOS)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("avg tempo"),
                    },
                    startOnTick: false,
                    endOnTick: false,
                    reversed: true,
                    tickInterval: tempoMax / 3,
                    labels: {
                      formatter: function (self: any) {
                        return Highcharts.dateFormat("%M:%S", self.value * 60 * 1000)
                      },
                      align: "center"
                    },
                    min: Math.max(tempoMax - 0.1, 0),
                    max: 15
                  }
                }} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip content={"info.tempo chart"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {tempo500Series && charts.includes(SeriesType.TEMPO_500S) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory(tempoFormatter,
                  isTracker ? "H:mm:ss.S" : undefined)}
                unit={"/500m"}
                valueFormatter={tempoFormatter}
                minMax={"min"}
                showXLabels={!(charts.includes(SeriesType.STROKES) ||
                  charts.includes(SeriesType.PULLING_FORCE) ||
                  charts.includes(SeriesType.DISTANCEPERSTROKE) ||
                  charts.includes(SeriesType.POWERS))}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                series={tempo500Series}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.TEMPO_500S)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("avg tempo split"),
                    },
                    startOnTick: false,
                    endOnTick: false,
                    reversed: true,
                    tickInterval: tempo500Max / 3,
                    labels: {
                      formatter: function (self: any) {
                        return Highcharts.dateFormat("%M:%S", self.value * 60 * 1000)
                      },
                      align: "center"
                    },
                    min: tempo500Max,
                    max: 8
                  }
                }} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip content={"info.tempo split chart"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {strokeSeries && charts.includes(SeriesType.STROKES) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory()}
                unit={"s/min"}
                valueFormatter={(x: number) => x.toFixed(1).replace('-0', '0')}
                showXLabels={!(charts.includes(SeriesType.PULLING_FORCE) ||
                  charts.includes(SeriesType.DISTANCEPERSTROKE) ||
                  charts.includes(SeriesType.POWERS))}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                series={strokeSeries}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.STROKES)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("avg stroke rate"),
                    },
                    min: 0,
                    max: strokeMax
                  }
                }} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip content={"info.stroke rate chart"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {distancePerStrokeSeries && charts.includes(SeriesType.DISTANCEPERSTROKE) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory((x: number) => x.toFixed(3).replace('-0', '0'))}
                unit={"m"}
                valueFormatter={(x: number) => x.toFixed(3).replace('-0', '0')}
                showXLabels={!(charts.includes(SeriesType.PULLING_FORCE) && charts.includes(SeriesType.POWERS))}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                series={distancePerStrokeSeries}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.DISTANCEPERSTROKE)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("avg distance per stroke"),
                    },
                    min: 0,
                    max: distancePerStrokeMax
                  }
                }} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip content={"info.distance per stroke"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {powerSeries && charts.includes(SeriesType.POWERS) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory((x: number) => x.toFixed(0))}
                unit={"W"}
                valueFormatter={(x: number) => x.toFixed(0).replace('-0', '0')}
                showXLabels={charts.includes(SeriesType.PULLING_FORCE)}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.POWERS)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("power"),
                    },
                    min: 0,
                    max: powerMax
                  }
                }}
                series={powerSeries
                  .map((x, i) => (
                    {
                      data: x,
                      color: forceSeriesColors[(i * (isTwoPaddle(sport) ? 2 : 1)) % forceSeriesColors.length],
                      dataGrouping: {
                        approximation: "max"
                      }
                    }
                  )).filter(x => !!x.data)} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent><InfoTooltip content={"info.power chart"} /></TooltipContent>
              </Tooltip>
            </div>
          }
          {forceSeries && charts.includes(SeriesType.PULLING_FORCE) &&
            <div className={styles.chartWrapper}>
              <LineChart ref={setChartRef(chartRefIndex++)}
                tooltipFormatter={tooltipFormatterFactory(forceFormatter)}
                unit={"N"}
                valueFormatter={forceFormatter}
                showXLabels={true}
                xAxisLabelFormatter={xAxisLabelFormatter}
                plotBands={bands}
                plotLines={plotLines.map(plotLineMapper)}
                setExtremes={setExtremes}
                series={forceSeries}
                showCrosshair={showCrosshair}
                onFullscreenClick={() => onFullscreenClick(SeriesType.PULLING_FORCE)}
                options={{
                  yAxis: {
                    ...standardLineChartOptions.yAxis,
                    title: {
                      text: t("avg pulling force"),
                    },
                    min: 0,
                    max: forceMax
                  }
                }} />
              <Tooltip>
                <TooltipTrigger>
                  <InfoTooltipButton className={styles.chartInfo} />
                </TooltipTrigger>
                <TooltipContent>
                  <InfoTooltip content={
                    athletes.length > 1 ? "info.pulling force chart multi" :
                      (isTwoPaddle(sport) ?
                        "info.pulling force chart double POD" :
                        "info.pulling force chart single POD")
                  } />
                </TooltipContent>
              </Tooltip>
            </div>
          }
        </div>
        {/*<div>*/}
        {/*    {charts.find(x => [SeriesType.HEARTRATE,*/}
        {/*            SeriesType.SPEEDS, SeriesType.TEMPOS, SeriesType.TEMPO_500S, SeriesType.PULLING_FORCE,*/}
        {/*            SeriesType.STROKES].includes(x)) &&*/}
        {/*        !mobileOrTablet && <ChartStepper*/}
        {/*            style={{marginLeft: 80, marginBottom: 20}}*/}
        {/*            min={0}*/}
        {/*            max={max}*/}
        {/*            from={sliceStart}*/}
        {/*            to={sliceEnd}*/}
        {/*            onChange={(from, to) => {*/}
        {/*                if (from < to)*/}
        {/*                    onZoomChange([from, to])*/}
        {/*            }}*/}
        {/*        />}*/}
        {/*</div>*/}
      </div>
      {hasZones &&
        <div className={styles.zonesContainer}>
          {userLactateSeries && userLactateSeries.map((x, i) => {
            return <div key={i} style={{ width: zoneWidth, height: chartHeight + 50 }} />
          })}
          {(heartRateSeries && charts.includes(SeriesType.HEARTRATE)) ?
            (charts.includes(SeriesType.HEARTRATE_ZONES) && hrZonesData ?
              <div className={styles.chartWrapper}>
                <HighchartsReact
                  ref={setZoneChartRef(zoneChartRefIndex++)}
                  highcharts={Highcharts}
                  updateArgs={[true, true, false]}
                  options={{
                    ...zoneChartOptions,
                    xAxis: {
                      categories: hrZonesData?.map(z => z.name),
                      ...(mobileOrTablet && {
                        title: {
                          text: t("heartrate"),
                        }
                      }),
                    },
                    series: [{
                      data: hrZonesData?.map(z => ({ y: z.value, color: z.color })),
                    }],
                    tooltip: {
                      useHTML: true,
                      formatter: function (): string {
                        const self = this as any
                        const x = self.x
                        const zone = hrZonesData?.find(z => z.name === x)
                        return `<b>${(self.y as number).toFixed(1)}%</b> (${zone?.tooltip})`
                      },
                    },
                  }}
                />
                <Tooltip>
                  <TooltipTrigger>
                    <InfoTooltipButton className={styles.chartInfoZones} />
                  </TooltipTrigger>
                  <TooltipContent><InfoTooltip
                    content={"info.hr zones chart"} /></TooltipContent>
                </Tooltip>
              </div> : (!mobileOrTablet ?
                <div style={{ width: zoneWidth, height: chartHeight }} /> : null)
            ) : null
          }
          {(speedSeries && charts.includes(SeriesType.SPEEDS)) ?
            (charts.includes(SeriesType.SPEED_ZONES) && speedZonesData ?
              <div className={styles.chartWrapper}>
                <HighchartsReact
                  ref={setZoneChartRef(zoneChartRefIndex++)}
                  highcharts={Highcharts}
                  updateArgs={[true, true, false]}
                  options={{
                    ...zoneChartOptions,
                    xAxis: {
                      categories: speedZonesData?.map(z => z.name),
                      ...(mobileOrTablet && {
                        title: {
                          text: t("speed"),
                        }
                      }),
                    },
                    series: [{
                      data: speedZonesData?.map(z => ({ y: z.value, color: z.color })),
                    }],
                    tooltip: {
                      useHTML: true,
                      formatter: function (): string {
                        const self = this as any
                        const x = self.x
                        const zone = speedZonesData?.find(z => z.name === x)
                        return `<b>${(self.y as number).toFixed(1)}%</b> (${zone?.tooltip})`
                      },
                    },
                  }}
                />
                <Tooltip>
                  <TooltipTrigger>
                    <InfoTooltipButton className={styles.chartInfoZones} />
                  </TooltipTrigger>
                  <TooltipContent><InfoTooltip
                    content={"info.speed zones chart"} /></TooltipContent>
                </Tooltip>
              </div> : (!mobileOrTablet ?
                <div style={{ width: zoneWidth, height: chartHeight }} />
                :
                null
              )
            ) :
            null
          }
          {
            (tempoSeries && charts.includes(SeriesType.TEMPOS)) ?
              (charts.includes(SeriesType.TEMPO_ZONES) && tempoZonesData ?
                <div className={styles.chartWrapper}>
                  <HighchartsReact
                    ref={setZoneChartRef(zoneChartRefIndex++)}
                    highcharts={Highcharts}
                    updateArgs={[true, true, false]}
                    options={{
                      ...zoneChartOptions,
                      xAxis: {
                        categories: tempoZonesData?.map(z => z.name),
                        ...(mobileOrTablet && {
                          title: {
                            text: t("tempo"),
                          }
                        }),
                      },
                      series: [{
                        data: tempoZonesData?.map(z => ({ y: z.value, color: z.color })),
                      }],
                      tooltip: {
                        useHTML: true,
                        formatter: function (): string {
                          const self = this as any
                          const x = self.x
                          const zone = tempoZonesData?.find(z => z.name === x)
                          return `<b>${(self.y as number).toFixed(1)}%</b> (${zone?.tooltip})`
                        },
                      },
                    }}
                  />
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoTooltipButton className={styles.chartInfoZones} />
                    </TooltipTrigger>
                    <TooltipContent><InfoTooltip
                      content={"info.tempo zones chart"} /></TooltipContent>
                  </Tooltip>
                </div> : (!mobileOrTablet ?
                  <div style={{ width: zoneWidth, height: chartHeight }} /> : null)
              ) : null
          }
          {
            (tempo500Series && charts.includes(SeriesType.TEMPO_500S)) ?
              (charts.includes(SeriesType.TEMPO_500_ZONES) && tempo500ZonesData ?
                <div className={styles.chartWrapper}>
                  <HighchartsReact
                    ref={setZoneChartRef(zoneChartRefIndex++)}
                    highcharts={Highcharts}
                    updateArgs={[true, true, false]}
                    options={{
                      ...zoneChartOptions,
                      xAxis: {
                        categories: tempo500ZonesData?.map(z => z.name),
                        ...(mobileOrTablet && {
                          title: {
                            text: t("tempo split"),
                          }
                        }),
                      },
                      series: [{
                        data: tempo500ZonesData?.map(z => ({ y: z.value, color: z.color })),
                      }],
                      tooltip: {
                        useHTML: true,
                        formatter: function (): string {
                          const self = this as any
                          const x = self.x
                          const zone = tempo500ZonesData?.find(z => z.name === x)
                          return `<b>${(self.y as number).toFixed(1)}%</b> (${zone?.tooltip})`
                        },
                      },
                    }}
                  />
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoTooltipButton className={styles.chartInfoZones} />
                    </TooltipTrigger>
                    <TooltipContent><InfoTooltip
                      content={"info.tempo split zones chart"} /></TooltipContent>
                  </Tooltip>
                </div> : (!mobileOrTablet ?
                  <div style={{ width: zoneWidth, height: chartHeight }} /> : null)
              ) : null
          }
          {
            strokeSeries && charts.includes(SeriesType.STROKES) &&
            <div style={{ width: zoneWidth, height: chartHeight }} className="d-none d-md-block" />
          }
          {
            distancePerStrokeSeries && charts.includes(SeriesType.DISTANCEPERSTROKE) &&
            <div style={{ width: zoneWidth, height: chartHeight }} className="d-none d-md-block" />
          }
          {
            powerSeries && charts.includes(SeriesType.POWERS) &&
            <div style={{ width: zoneWidth, height: chartHeight }} className="d-none d-md-block" />
          }
          {
            (forceSeries && charts.includes(SeriesType.PULLING_FORCE)) ?
              (charts.includes(SeriesType.FORCE_ZONES) && forceZonesData ?
                <div className={styles.chartWrapper}>
                  <HighchartsReact
                    ref={setZoneChartRef(zoneChartRefIndex++)}
                    highcharts={Highcharts}
                    updateArgs={[true, true, false]}
                    options={{
                      ...zoneChartOptions,
                      xAxis: {
                        categories: forceZonesData?.map(z => z.name),
                        ...(mobileOrTablet && {
                          title: {
                            text: t("force zones"),
                          }
                        }),
                      },
                      series: [{
                        data: forceZonesData?.map(z => ({ y: z.value, color: z.color })),
                      }],
                      yAxis: {
                        ...zoneChartOptions.yAxis,
                        max: undefined,
                      },
                      tooltip: {
                        useHTML: true,
                        formatter: function (): string {
                          const self = this as any
                          const x = self.x
                          const zone = forceZonesData?.find(z => z.name === x)
                          return `<b>${(self.y as number).toFixed(0).replace('-0', '0')}%</b> (${zone?.tooltip})`
                        },
                      },
                    }}
                  />
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoTooltipButton className={styles.chartInfoZones} />
                    </TooltipTrigger>
                    <TooltipContent><InfoTooltip
                      content={"info.pulling force zones chart"} /></TooltipContent>
                  </Tooltip>
                </div> : (!mobileOrTablet ?
                  <div style={{ width: zoneWidth, height: chartHeight }} /> : null)) : null
          }
        </div>
      }
    </div>
  )
}
